import React, { useEffect, useMemo, useRef, useState } from "react";
import * as Scrivito from "scrivito";
import cx from "classnames";
import { useQuery } from "urql";
import { UncontrolledTooltip } from "reactstrap";
import { priceToString } from "utils/priceToString";
import {
  ProductListCalculationMarker,
  ProductListDownloadButton,
  ProductListFilterButton,
  ProductListSizeSwitcher,
} from "components/ProductListOfProject/SubComponents";
import { Icon } from "components/Icon/Icon";
import { TranslateWrapper } from "components/TranslateWrapper";
import { ProductPriceListQry, UserProductListQry } from "api/queries/product";
import {
  priceSortingCriteria,
  PRODUCTS_PER_PAGE,
  SORTING_CRITERIA,
} from "variables";
import { useItemsPerPage, useTranslate } from "hooks";
import { ProductListOfProject } from "./ProductListOfProject";
import { ItemsPerPageSelect } from "../ItemsPerPageSelect";
import { useSetIsFetching } from "stores/loadingStateStore";
import { useIsSameProj, useLanguage } from "stores/globalStore";
import { useProject } from "stores/project/project/projectStore";
import type {
  ProductListTableProduct,
  ProductListRefValue,
} from "interfaces/product/product.types";

type ProductListWithControlsProps = {
  nodeId?: string;
  hasSizeSwitcher?: boolean;
  isModalView?: boolean;
  productListRef?: React.RefObject<ProductListRefValue | null> | null;
  hasFilter?: boolean;
};

export const ProductListWithControls = Scrivito.connect(
  ({
    nodeId,
    hasFilter = false,
    hasSizeSwitcher = false,
    isModalView = false,
    productListRef = null,
  }: ProductListWithControlsProps) => {
    const project = useProject();
    const translate = useTranslate();
    const setIsFetching = useSetIsFetching();
    const [isOrderDescending, setIsOrderDescending] = useState(false);
    const [sortCriteria, setSortCriteria] = useState(SORTING_CRITERIA.NAME);
    const tableRef = useRef<HTMLDivElement | null>(null);

    const lang = useLanguage();
    const isSameProj = useIsSameProj();
    const isPriceSortingCriteria = priceSortingCriteria.includes(sortCriteria);

    const {
      itemsPerPage,
      handleItemsPerPageChange,
      currentPageNo,
      setCurrentPageNo,
    } = useItemsPerPage(PRODUCTS_PER_PAGE);

    const [{ data: productListData, fetching, stale }, refetchUserProductList] =
      useQuery({
        query: UserProductListQry,
        variables: !isPriceSortingCriteria
          ? {
              input: {
                sort: {
                  sortBy: sortCriteria,
                  sortOrder: isOrderDescending ? "desc" : "asc",
                },
                projectId: project?.id,
                limit: itemsPerPage,
                page: currentPageNo,
                projectNodeId: nodeId,
                languageCode: lang,
              },
            }
          : {
              input: {
                projectId: project?.id,
                projectNodeId: nodeId,
                limit: 0,
                languageCode: lang,
              },
            },
        pause: !project?.id,
      });

    const productListFetching = fetching || stale;

    const productList = productListData?.productList;
    const productsDatas = useMemo(
      () => productList?.products ?? [],
      [productList?.products],
    );

    const productsIds = useMemo(
      () => productsDatas.map((product) => product?.id),
      [productsDatas],
    );

    // TODO: This additional request for prices needs for AB
    const [
      {
        data: priceListData,
        stale: priceListStale,
        fetching: priceListFetching,
      },
      refetchProductPriceList,
    ] = useQuery({
      query: ProductPriceListQry,
      variables: isPriceSortingCriteria
        ? {
            input: {
              projectId: project?.id,
              projectNodeId: nodeId,
              sort: {
                sortBy: sortCriteria,
                sortOrder: isOrderDescending ? "desc" : "asc",
              },

              limit: itemsPerPage,
              page: currentPageNo,
              languageCode: lang,
            },
          }
        : {
            input: {
              projectId: project?.id,
              projectNodeId: nodeId,
              filter: {
                productNodeIds: productsIds.filter((id): id is string => !!id),
              },
              languageCode: lang,
            },
          },
      pause: !project?.id && !productListFetching,
    });

    if (productListRef) {
      productListRef.current = {
        refetchProductPriceList,
        refetchUserProductList,
      };
    }

    const priceOnRequest =
      priceListData?.productPriceList?.includesPriceOnRequest;

    const { productPriceList } = priceListData ?? {};
    const priceListProducts = useMemo(
      () => productPriceList?.products ?? [],
      [productPriceList?.products],
    );

    const productsWithPrices = useMemo(() => {
      if (!productsDatas.length) {
        return [];
      }

      return productsDatas.map((product) => {
        const matchingPrice = priceListProducts.find(
          (priceItem) => priceItem?.id === product?.id,
        );

        return {
          ...product,
          fullPriceGroup: matchingPrice?.fullPriceGroup || "",
          priceListDesc: matchingPrice?.priceListDesc || "",
          totalPrice: matchingPrice?.totalPrice || {},
          unitPrice: matchingPrice?.unitPrice || {},
        };
      });
    }, [productsDatas, priceListProducts]);

    const listToDisplay = useMemo(() => {
      const productsArray = Array.isArray(productsDatas) ? productsDatas : [];
      const priceListArray = Array.isArray(priceListProducts)
        ? priceListProducts
        : [];

      if (isPriceSortingCriteria) {
        return priceListArray.map((priceProduct) => {
          const matchingProduct = productsArray.find(
            (product) => product?.id === priceProduct?.id,
          );

          return {
            ...priceProduct,
            title: matchingProduct?.title ?? "",
            presentation: matchingProduct?.presentation ?? "",
            quantity: matchingProduct?.quantity ?? 0,
          };
        });
      }

      return productsWithPrices;
    }, [
      isPriceSortingCriteria,
      priceListProducts,
      productsDatas,
      productsWithPrices,
    ]);

    const priceListLoading = priceListStale || priceListFetching;
    const tableFetching =
      productListFetching || (isPriceSortingCriteria && priceListLoading);

    const pagesCountForPriceList = Math.ceil(
      productsDatas.length / itemsPerPage,
    );

    const { productsCount = 0, pages: pagesCount = 0 } = productList ?? {}; // "?? {}" because sometimes productList is null

    useEffect(
      () => setIsFetching(priceListStale),
      [priceListStale, setIsFetching],
    );

    const isDownloadAllowed = useMemo(
      () => priceListProducts.length > 0,
      [priceListProducts.length],
    );

    const productsPrice = useMemo(
      () => productPriceList?.productsPrice ?? null,
      [productPriceList?.productsPrice],
    );

    const totalPrice = useMemo(() => {
      if (productsPrice) {
        return priceToString({
          price: {
            currencyCode: productsPrice?.currencyCode,
            fractionDigits: productsPrice?.fractionDigits,
            centAmount: productsPrice?.centAmount as number,
          },
          noPriceText: undefined,
        });
      }
    }, [productsPrice]);

    const totalPriceLabel = !isSameProj
      ? translate("TOTAL_PRICE")
      : translate("TOTAL_PRICE_SAME");

    return (
      <>
        {isModalView && productList?.includesDummyProduct && (
          <ProductListCalculationMarker />
        )}
        <div
          className={cx({
            "pb-6 mt-3": !isModalView,
            "pb-0 mt-1": isModalView,
          })}
        >
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center font-bold">
              {hasFilter && <ProductListFilterButton />}
              {hasSizeSwitcher && <ProductListSizeSwitcher />}
            </div>
            <div className="d-flex align-items-center">
              <div className="d-flex flex-column">
                <div className="me-3 d-flex align-items-center">
                  {totalPriceLabel}:
                  {productListFetching ||
                  priceListStale ||
                  priceListFetching ? (
                    <div
                      style={{
                        width: "80px",
                        height: "18px",
                      }}
                      className="blockSkeleton ms-2_5 rounded"
                    ></div>
                  ) : (
                    <>
                      {totalPrice ? (
                        <div
                          className={cx(
                            "d-flex align-items-center flex-nowrap font-bold ms-1 ",
                            {
                              "text-warning": priceOnRequest,
                            },
                          )}
                        >
                          {totalPrice}
                          <Icon
                            className={cx(
                              "ms-2 text-gray-600 cursor-pointer focus-ring focus-ring-light",
                              {
                                "d-none": !priceOnRequest,
                              },
                            )}
                            id="total_price"
                            tabIndex={-1}
                            iconName="circle-info-regular"
                          />
                          <UncontrolledTooltip
                            target="total_price"
                            placement="top"
                          >
                            {translate("TOTAL_PRICE_ATTENTION")}
                          </UncontrolledTooltip>
                          {typeof translate("TOTAL_PRICE_ATTENTION") ===
                            "object" && (
                            <TranslateWrapper
                              tooltipText="TOTAL_PRICE_ATTENTION"
                              tooltipId="total_price"
                              placement="top"
                            />
                          )}
                        </div>
                      ) : (
                        <div className={cx({ "text-warning": priceOnRequest })}>
                          {translate("NO_PRICE")}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
              <ProductListDownloadButton
                projectId={project?.id ?? ""}
                downloadAllowed={isDownloadAllowed}
                nodeId={nodeId}
              />
            </div>
          </div>
          <div className="pt-2_5 bg-white">
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              onChange={handleItemsPerPageChange}
            />

            <ProductListOfProject
              currentPageNo={currentPageNo}
              fetchingProductList={tableFetching}
              fetchingPrices={priceListLoading}
              isOrderDescending={isOrderDescending}
              pagesCount={
                isPriceSortingCriteria
                  ? pagesCountForPriceList
                  : (pagesCount ?? 0)
              }
              products={listToDisplay as ProductListTableProduct[]}
              productsCount={productsCount ?? 0}
              setCurrentPageNo={setCurrentPageNo}
              setIsOrderDescending={setIsOrderDescending}
              tableRef={tableRef}
              itemsPerPage={itemsPerPage}
              setSortCriteria={setSortCriteria}
              sortCriteria={sortCriteria}
              isModalView={isModalView}
              priceGroupDescription={
                priceListData?.productPriceList?.description ?? ""
              }
            />
          </div>
        </div>
      </>
    );
  },
);
